import { c as _c } from "react/compiler-runtime";
import { DEFAULT_TABLE_PAGE_SIZE } from "@components/common/faro-table/faro-table";
import { useCallback, useMemo, useState } from "react";
import { calculateTotalCount, calculateDisplayIndices, calculateLastPage, shouldDisableNextButton, getPageChangeDirection, loadNextPage } from "@utils/pagination-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";

/**
 * Unified pagination hook for table components
 *
 * Supports both client-side and server-side pagination with known/unknown totals.
 * Returns all data needed for pagination controls and footer display.
 */
export function useTablePagination(t0) {
  const $ = _c(44);
  const {
    mode,
    entities,
    pageIndex,
    setPageIndex,
    pageSize: t1,
    serverSidePaginationProps,
    onPageChange
  } = t0;
  const pageSize = t1 === undefined ? DEFAULT_TABLE_PAGE_SIZE : t1;
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const {
    handleErrorWithToast
  } = useErrorContext();
  serverSidePaginationProps?.totalCount;
  serverSidePaginationProps?.hasMoreItems;
  const t2 = serverSidePaginationProps?.totalCount;
  const t3 = serverSidePaginationProps?.hasMoreItems;
  let t4;
  if ($[0] !== entities.length || $[1] !== mode || $[2] !== t2 || $[3] !== t3) {
    t4 = calculateTotalCount({
      mode,
      entitiesLength: entities.length,
      totalCount: t2,
      hasMoreItems: t3
    });
    $[0] = entities.length;
    $[1] = mode;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const effectiveTotalCount = t4;
  let t5;
  if ($[5] !== effectiveTotalCount || $[6] !== entities || $[7] !== pageIndex || $[8] !== pageSize || $[9] !== serverSidePaginationProps?.hasMoreItems) {
    t5 = () => {
      const start = pageIndex * pageSize;
      const end = start + pageSize;
      const paginated = entities.slice(start, end);
      const {
        from,
        to
      } = calculateDisplayIndices({
        pageIndex,
        pageSize,
        paginatedLength: paginated.length
      });
      const lastPage = calculateLastPage({
        effectiveTotalCount,
        pageSize,
        entitiesLength: entities.length,
        hasMoreItems: serverSidePaginationProps?.hasMoreItems
      });
      return {
        paginatedEntities: paginated,
        from,
        to,
        lastPage
      };
    };
    $[5] = effectiveTotalCount;
    $[6] = entities;
    $[7] = pageIndex;
    $[8] = pageSize;
    $[9] = serverSidePaginationProps?.hasMoreItems;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  serverSidePaginationProps?.hasMoreItems;
  let t6;
  if ($[11] !== t5) {
    t6 = t5();
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const {
    paginatedEntities,
    from: from_0,
    to: to_0,
    lastPage: lastPage_0
  } = t6;
  serverSidePaginationProps?.hasMoreItems;
  const t7 = serverSidePaginationProps?.hasMoreItems;
  let t8;
  if ($[13] !== entities.length || $[14] !== lastPage_0 || $[15] !== pageIndex || $[16] !== pageSize || $[17] !== t7) {
    t8 = shouldDisableNextButton({
      pageIndex,
      lastPage: lastPage_0,
      entitiesLength: entities.length,
      pageSize,
      hasMoreItems: t7
    });
    $[13] = entities.length;
    $[14] = lastPage_0;
    $[15] = pageIndex;
    $[16] = pageSize;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const shouldDisableNextPage = t8;
  let t9;
  if ($[19] !== entities.length || $[20] !== handleErrorWithToast || $[21] !== mode || $[22] !== onPageChange || $[23] !== pageIndex || $[24] !== pageSize || $[25] !== serverSidePaginationProps?.fetchNextPage || $[26] !== serverSidePaginationProps?.totalCount || $[27] !== setPageIndex) {
    t9 = async newPage => {
      const direction = getPageChangeDirection({
        currentPage: pageIndex,
        newPage
      });
      if (mode === "server" && newPage > pageIndex) {
        await loadNextPage({
          newPage,
          pageSize,
          entitiesLength: entities.length,
          setIsLoadingNextPage,
          totalCount: serverSidePaginationProps?.totalCount ?? undefined,
          fetchNextPage: serverSidePaginationProps?.fetchNextPage
        }).catch(error => {
          handleErrorWithToast({
            id: `pagination-fetch-error-${Date.now()}`,
            title: "Failed to load next page",
            error
          });
        });
      }
      onPageChange?.(newPage, direction);
      setPageIndex(newPage);
    };
    $[19] = entities.length;
    $[20] = handleErrorWithToast;
    $[21] = mode;
    $[22] = onPageChange;
    $[23] = pageIndex;
    $[24] = pageSize;
    $[25] = serverSidePaginationProps?.fetchNextPage;
    $[26] = serverSidePaginationProps?.totalCount;
    $[27] = setPageIndex;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const handlePageChange = t9;
  let t10;
  if ($[29] !== pageIndex || $[30] !== pageSize) {
    t10 = {
      page: pageIndex,
      pageSize
    };
    $[29] = pageIndex;
    $[30] = pageSize;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const paginationModel = t10;
  let t11;
  if ($[32] !== effectiveTotalCount || $[33] !== from_0 || $[34] !== lastPage_0 || $[35] !== to_0) {
    t11 = {
      from: from_0,
      to: to_0,
      rowCount: effectiveTotalCount,
      lastPage: lastPage_0
    };
    $[32] = effectiveTotalCount;
    $[33] = from_0;
    $[34] = lastPage_0;
    $[35] = to_0;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== handlePageChange || $[38] !== isLoadingNextPage || $[39] !== paginatedEntities || $[40] !== paginationModel || $[41] !== shouldDisableNextPage || $[42] !== t11) {
    t12 = {
      paginatedEntities,
      footerData: t11,
      paginationModel,
      handlePageChange,
      shouldDisableNextPage,
      isLoadingNextPage
    };
    $[37] = handlePageChange;
    $[38] = isLoadingNextPage;
    $[39] = paginatedEntities;
    $[40] = paginationModel;
    $[41] = shouldDisableNextPage;
    $[42] = t11;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  return t12;
}